// Slides in after the user picks a type — replaces the navigate() call entirely.

import { useForm } from "react-hook-form";
import React, { useRef, useContext, useState, useEffect, useCallback } from "react";
import moment from 'moment';
import { debitNoteService } from "../../services/debitNote.service";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import LogicContext from "../../Context/LogicContext";
import CustomerSearchSelect from "../customer-debit-notes/CustomerSearchSelect";
import { GetEditDebitNoteAtom, SetEditDebitNoteAtom } from "../../store/debitNoteStore";


// ─── Icons ────────────────────────────────────────────────────────────────────
const PrintIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
    </svg>
);
const ChevronDown = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);
const ChevronLeft = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
);
const SearchIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0" />
    </svg>
);
const XIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);
const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);
const UserIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);
const MailIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
);


const inputCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition";
const selectCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition appearance-none";


// ─── Label + Input helpers ─────────────────────────────────────────────────────
const FormField = ({ label, children, required }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        {children}
    </div>
);


export const CreateDebitNotePanel = ({ isOpen, onClose, onBack, selectedType, bankName, accountId, onSaved }) => {
    const [saving, setSaving] = useState(false);
    const [customer, setCustomer] = useState({});
    const isMounted = useRef(true);

    const myPromiseModal = createModal(GeneralModal);
    const logicContext = useContext(LogicContext);
    const getDebitNoteEdit = GetEditDebitNoteAtom();
    const setDebitNoteEdit = SetEditDebitNoteAtom();

    const { register, handleSubmit, reset, control, getValues, setValue, formState: { errors } } = useForm({
        defaultValues: {
            transaction_date: moment().format('YYYY-MM-DD'),
            customer_id: "",
            amount: "",
            remark: "",
        }
    });

    // Lifecycle management
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Reset form and state when panel opens OR closes
    useEffect(() => {
        if (isOpen && !getDebitNoteEdit?.ref_no) {
            // Opening a fresh form
            reset({
                transaction_date: moment().format('YYYY-MM-DD'),
                customer_id: "",
                amount: "",
                remark: "",
                ref_no: "",
            });
            setCustomer({});
        } else if (!isOpen) {
            // Cleaning up when panel is closed
            reset({
                transaction_date: moment().format('YYYY-MM-DD'),
                customer_id: "",
                amount: "",
                remark: "",
                ref_no: "",
            });
            setCustomer({});
            setDebitNoteEdit({});
        }
    }, [isOpen]);

    // Populate form when editing
    useEffect(() => {
        if (getDebitNoteEdit?.ref_no && isOpen) {
            setValue("customer_id", getDebitNoteEdit?.customer_id);
            setValue("amount", getDebitNoteEdit?.amount);
            setValue("remark", getDebitNoteEdit?.remark);
            setValue("ref_no", getDebitNoteEdit?.ref_no);
            setValue("transaction_date", getDebitNoteEdit?.transaction_date
                ? moment(getDebitNoteEdit?.transaction_date).format("YYYY-MM-DD")
                : moment().format("YYYY-MM-DD"));

            const tempCustomer = {
                Email_Address: getDebitNoteEdit?.customer_email_address,
                Name: getDebitNoteEdit?.customer_full_name,
                Customer_Id: getDebitNoteEdit?.customer_id
            };
            setCustomer(tempCustomer);
        }
    }, [getDebitNoteEdit?.ref_no, isOpen]);

    const onCustomerSelected = (customer) => {
        setValue("customer_id", customer?.Customer_Id);
        setCustomer(customer);
    };

    const onSubmit = async (data, event) => {
        // Determine which button was clicked if available
        const buttonName = event?.nativeEvent?.submitter?.name;
        const isPublish = buttonName === "save_and_publish";

        const modalBody = isPublish
            ? "Do you want to save and publish this record?"
            : "Do you want to save this record?";

        const result = await myPromiseModal({
            title: 'Confirmation',
            body: modalBody,
            showInput: false,
            placeHolder: '',
        });

        if (!isMounted.current || !result?.decision) return;

        setSaving(true);

        try {
            const newData = getValues();
            let option = isPublish ? 3 : 1; // 3 for Save and Publish, 1 for Save
            if (getDebitNoteEdit?.ref_no) {
                option = isPublish ? 4 : 2; // 4 for Update and Publish, 2 for Update
            }

            const res = await debitNoteService.submitDebitNote({ ...newData, option });

            if (!isMounted.current) return;

            if (res?.data !== undefined) {
                const { message_text: Message_text, message_id } = res.data[0] || res.data?.result_status?.[0];
                if (message_id === 1) {
                    logicContext.showToast(Message_text, "success");
                    // Clear state immediately on success
                    setDebitNoteEdit({});
                    reset({
                        transaction_date: moment().format('YYYY-MM-DD'),
                        customer_id: "",
                        amount: "",
                        remark: "",
                        ref_no: "",
                    });
                    setCustomer({});
                    onSaved?.();
                    onClose();
                } else {
                    logicContext.showToast(Message_text, "error");
                }
            }
        } catch (err) {
            if (isMounted.current) {
                logicContext.showToast(`Error ${isPublish ? "publishing" : "submitting"} record`, "error");
            }
        } finally {
            setSaving(false);
        }
    };

    const onErrors = (error) => {
        console.error("Form error: ", error);
        logicContext.showToast("Form error", "error");
    };

    const isEditMode = !!getDebitNoteEdit?.ref_no;
    const isStatusOne = getDebitNoteEdit?.status_id === 1;

    return (
        <div
            className={`fixed top-0 right-0 h-screen w-[480px] bg-white z-50 flex flex-col overflow-hidden
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            style={{ borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 24px rgba(0,0,0,0.10)' }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <button
                        onClick={onBack}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
                        title="Back to type selection"
                    >
                        <ChevronLeft />
                    </button>
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest leading-none mb-0.5">
                            {isEditMode ? 'Edit' : 'New'} Debit Note
                        </p>
                        <h3 className="text-base font-bold text-gray-800 leading-tight">
                            {selectedType?.transaction_name || 'Debit Note'}
                        </h3>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
                >
                    <XIcon />
                </button>
            </div>


            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit, onErrors)} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

                {/* Customer Selection — z-10 + relative so the dropdown floats above all sibling fields */}
                <div className="relative z-10">
                    <FormField label="Customer Name" required>
                        <div className="w-full">
                            <CustomerSearchSelect
                                {...register('customer_id', { required: 'Customer is required' })}
                                onCustomerSelected={onCustomerSelected}
                            />
                        </div>
                        {errors.customer_id && <p className="text-xs text-red-500 mt-1">{errors.customer_id.message}</p>}

                        {/* Customer Details Card */}
                        {customer?.Customer_Id && (
                            <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2">
                                <div className="flex items-center gap-2">
                                    <UserIcon />
                                    <span className="text-sm font-semibold text-blue-800">{customer?.Name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <MailIcon />
                                    <span className="text-xs text-blue-600">{customer?.Email_Address}</span>
                                </div>
                            </div>
                        )}
                    </FormField>
                </div>

                {/* Amount — z-0 keeps it behind the customer search dropdown */}
                <div className="relative z-0">
                    <FormField label="Amount" required>
                        <div className="flex">
                            <span className="inline-flex items-center px-3 py-2.5 border border-r-0 border-gray-200 rounded-l-lg bg-gray-50 text-sm font-medium text-gray-500">
                                NGN
                            </span>
                            <input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                className={`${inputCls} rounded-l-none`}
                                {...register('amount', {
                                    required: 'Amount is required',
                                    min: { value: 0.01, message: 'Amount must be greater than 0' },
                                    valueAsNumber: true,
                                })}
                            />
                        </div>
                        {errors.amount && <p className="text-xs text-red-500 mt-1">{errors.amount.message}</p>}
                    </FormField>
                </div>

                {/* Transaction Date */}
                <FormField label="Transaction Date" required>
                    <input
                        type="date"
                        className={inputCls}
                        {...register('transaction_date', { required: 'Transaction date is required' })}
                    />
                    {errors.transaction_date && <p className="text-xs text-red-500 mt-1">{errors.transaction_date.message}</p>}
                </FormField>

                {/* Remark */}
                <FormField label="Remark">
                    <textarea
                        rows={3}
                        placeholder="Enter remarks"
                        className={`${inputCls} resize-none`}
                        {...register('remark')}
                    />
                </FormField>

                {/* spacer so last field isn't hidden behind sticky footer */}
                <div className="h-2" />
            </form>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex gap-2 flex-shrink-0">
                {/* Save & Apply / Apply Debit button */}
                <button
                    type="button"
                    onClick={(e) => {
                        const syntheticEvent = { nativeEvent: { submitter: { name: 'save_and_publish' } } };
                        handleSubmit((data) => onSubmit(data, syntheticEvent), onErrors)();
                    }}
                    disabled={saving}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5 px-3 rounded-lg transition shadow-sm"
                >
                    {saving ? 'Saving…' : isStatusOne ? 'Apply Debit' : 'Save & Apply'}
                </button>

                {/* Save / Update button */}
                <button
                    type="button"
                    onClick={(e) => {
                        const syntheticEvent = { nativeEvent: { submitter: { name: 'save' } } };
                        handleSubmit((data) => onSubmit(data, syntheticEvent), onErrors)();
                    }}
                    disabled={saving}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5 px-3 rounded-lg transition shadow-sm"
                >
                    {saving ? 'Saving…' : isEditMode ? 'Update' : 'Save'}
                </button>

                <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-white hover:bg-gray-100 text-gray-700 text-sm font-semibold py-2.5 px-4 rounded-lg border border-gray-200 transition"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};